import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useOptimizationStore } from '../store/optimizationStore';
import { sortMetaPromptKeys } from '../utils/sortMetaPromptKeys';
import MultiAgentMini from './MultiAgentMini';
import AgentSelector from './AgentSelector';
import ConnectorConfigEvaluationView from './connector/ConnectorConfigEvaluationView';

interface AgentSettings {
  prompt: string;
  model: string;
  temperature: number;
  description: string;
  name: string;
  reminder_prompt?: string;
}

function formatReminderDisplay(value: string | undefined): string {
  const t = (value ?? '').trim();
  return t.length > 0 ? t : '—';
}

interface MultiAgentConfigVisualizationProps {
  isConnector?: boolean; // Новый prop для ConnectorPage
  /** Connector only: apply an optimization iteration to the Integration Configurator */
  onLoadOptimizedStepIntoConfigurator?: (iterationResult: any) => void;
}

const MultiAgentConfigVisualization: React.FC<MultiAgentConfigVisualizationProps> = ({
  isConnector = false,
  onLoadOptimizedStepIntoConfigurator,
}) => {
    const {
    initialPrompt,
    botPrompt,
    multiAgentOptimizedPrompts,
    multiAgentIterationResults,
    multiAgentIsOptimized,
    multiAgentSelectedIterationIndex,
    setMultiAgentSelectedIterationIndex,
    multiAgentSelectedPrompt,
    setMultiAgentSelectedPrompt,
    multiAgentSelectedPromptIndex,
    setMultiAgentSelectedPromptIndex,
    multiAgentGraphSliderValue,
    setMultiAgentGraphSliderValue,
    selectedDataset,
    selectedModel,
    temperature,
    optimizedTemperature,
    workers,
    coordinatorPrompt,
    coordinatorModel,
    coordinatorTemperature,
    coordinatorDescription,
    coordinatorReminderPrompt,
    coordinatorName,
    // ConnectorPage поля
    connectorMultiAgentOptimizedPrompts,
    connectorMultiAgentIterationResults,
    connectorMultiAgentIsOptimized,
    connectorMultiAgentIsOptimizing,
    connectorMultiAgentSelectedIterationIndex,
    setConnectorMultiAgentSelectedIterationIndex,
    connectorMultiAgentSelectedPrompt,
    setConnectorMultiAgentSelectedPrompt,
    connectorMultiAgentSelectedPromptIndex,
    setConnectorMultiAgentSelectedPromptIndex,
    connectorMultiAgentGraphSliderValue,
    setConnectorMultiAgentGraphSliderValue,
    connectorWorkers,
    connectorCoordinatorPrompt,
    connectorCoordinatorModel,
    connectorCoordinatorTemperature,
    connectorCoordinatorDescription,
    connectorCoordinatorReminderPrompt,
    connectorCoordinatorName,
    connectorMetaPrompts
  } = useOptimizationStore();
  
  // Выбираем правильные данные в зависимости от типа страницы
  const currentOptimizedPrompts = isConnector ? connectorMultiAgentOptimizedPrompts : multiAgentOptimizedPrompts;
  const currentIterationResults = isConnector ? connectorMultiAgentIterationResults : multiAgentIterationResults;
  const currentIsOptimized = isConnector ? connectorMultiAgentIsOptimized : multiAgentIsOptimized;
  const currentSelectedIterationIndex = isConnector ? connectorMultiAgentSelectedIterationIndex : multiAgentSelectedIterationIndex;
  const setCurrentSelectedIterationIndex = isConnector ? setConnectorMultiAgentSelectedIterationIndex : setMultiAgentSelectedIterationIndex;
  const currentSelectedPrompt = isConnector ? connectorMultiAgentSelectedPrompt : multiAgentSelectedPrompt;
  const setCurrentSelectedPrompt = isConnector ? setConnectorMultiAgentSelectedPrompt : setMultiAgentSelectedPrompt;
  const currentSelectedPromptIndex = isConnector ? connectorMultiAgentSelectedPromptIndex : multiAgentSelectedPromptIndex;
  const setCurrentSelectedPromptIndex = isConnector ? setConnectorMultiAgentSelectedPromptIndex : setMultiAgentSelectedPromptIndex;
  const currentGraphSliderValue = isConnector ? connectorMultiAgentGraphSliderValue : multiAgentGraphSliderValue;
  const setCurrentGraphSliderValue = isConnector ? setConnectorMultiAgentGraphSliderValue : setMultiAgentGraphSliderValue;
  const currentWorkers = isConnector ? connectorWorkers : workers;
  const currentCoordinatorPrompt = isConnector ? connectorCoordinatorPrompt : coordinatorPrompt;
  const currentCoordinatorModel = isConnector ? connectorCoordinatorModel : coordinatorModel;
  const currentCoordinatorTemperature = isConnector ? connectorCoordinatorTemperature : coordinatorTemperature;
  const currentCoordinatorDescription = isConnector ? connectorCoordinatorDescription : coordinatorDescription;
  const currentCoordinatorReminderPrompt = isConnector
    ? connectorCoordinatorReminderPrompt
    : coordinatorReminderPrompt;
  const currentCoordinatorName =
    (isConnector ? connectorCoordinatorName : coordinatorName)?.trim() || 'Координатор';

  // Отладочное логирование для проверки правильности выбора данных
  useEffect(() => {
    console.log('🔍 MultiAgentConfigVisualization data selection:', {
      isConnector,
      currentIsOptimized,
      currentIterationResultsCount: currentIterationResults.length,
      multiAgentIterationResultsCount: multiAgentIterationResults.length,
      connectorMultiAgentIterationResultsCount: connectorMultiAgentIterationResults.length,
      currentWorkersCount: currentWorkers.length,
      workersCount: workers.length,
      connectorWorkersCount: connectorWorkers.length,
      currentCoordinatorPrompt: currentCoordinatorPrompt?.substring(0, 50),
      coordinatorPrompt: coordinatorPrompt?.substring(0, 50),
      connectorCoordinatorPrompt: connectorCoordinatorPrompt?.substring(0, 50),
      '⚠️ CHECK': isConnector 
        ? 'Should use connector* data' 
        : 'Should use multiAgent* data'
    });
    
    // Проверяем, что используются правильные данные
    if (isConnector) {
      // На ConnectorPage должны использоваться connector* данные
      if (currentIterationResults !== connectorMultiAgentIterationResults) {
        console.error('❌ ERROR: ConnectorPage is using wrong iterationResults!');
      }
      if (currentWorkers !== connectorWorkers) {
        console.error('❌ ERROR: ConnectorPage is using wrong workers!');
      }
      if (currentCoordinatorPrompt !== connectorCoordinatorPrompt) {
        console.error('❌ ERROR: ConnectorPage is using wrong coordinatorPrompt!');
      }
    } else {
      // На MultiAgentPage должны использоваться multiAgent* данные
      if (currentIterationResults !== multiAgentIterationResults) {
        console.error('❌ ERROR: MultiAgentPage is using wrong iterationResults!');
      }
      if (currentWorkers !== workers) {
        console.error('❌ ERROR: MultiAgentPage is using wrong workers!');
      }
      if (currentCoordinatorPrompt !== coordinatorPrompt) {
        console.error('❌ ERROR: MultiAgentPage is using wrong coordinatorPrompt!');
      }
    }
  }, [isConnector, currentIsOptimized, currentIterationResults, multiAgentIterationResults, connectorMultiAgentIterationResults, currentWorkers, workers, connectorWorkers, currentCoordinatorPrompt, coordinatorPrompt, connectorCoordinatorPrompt]);



  // Состояние для отслеживания выбранного агента в каждой конфигурации
  const [selectedAgent, setSelectedAgent] = useState<{
    initial: { type: 'coordinator' | 'worker' | 'meta'; index?: number; metaPromptKey?: string } | null;
    optimized: { type: 'coordinator' | 'worker' | 'meta'; index?: number; metaPromptKey?: string } | null;
  }>({
    initial: { type: 'coordinator' },
    optimized: { type: 'coordinator' }
  });

  // Отладочная информация - reduced for performance
  useEffect(() => {
    if (currentIsOptimized) {
      console.log('🔍 MultiAgentConfigVisualization: Optimization completed', { isConnector });
    }
  }, [currentIsOptimized, isConnector]);

  // Состояние для анимации
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('right');
  const [counterKey, setCounterKey] = useState(0);
  const [newPromptIndex, setNewPromptIndex] = useState<number | null>(null);
  const [previousPromptsCount, setPreviousPromptsCount] = useState(0);
  const [animatingIndex, setAnimatingIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  // Используем ref для отслеживания состояния анимации в setTimeout
  const animationRef = useRef<{
    isAnimating: boolean;
    animatingIndex: number | null;
  }>({ isAnimating: false, animatingIndex: null });

  // Фильтруем результаты: baseline (seq: 0) и оптимизированные (seq > 0)
  const baselineResult = useMemo(() => {
    return currentIterationResults.find(r => r.seq === 0 || r.iteration === 0);
  }, [currentIterationResults]);

  const optimizedResults = useMemo(() => {
    return currentIterationResults
      .filter((r) => {
        const seq = r.seq !== undefined ? r.seq : r.iteration;
        return typeof seq === 'number' && seq > 0;
      })
      .sort((a, b) => {
        const seqA = a.seq !== undefined ? a.seq : a.iteration;
        const seqB = b.seq !== undefined ? b.seq : b.iteration;
        return (seqA ?? 0) - (seqB ?? 0);
      });
  }, [currentIterationResults]);
  useEffect(() => {
    if (optimizedResults.length > 0) {
      const lastIndex = optimizedResults.length - 1;

      if (currentSelectedIterationIndex >= optimizedResults.length) {
        setCurrentSelectedIterationIndex(lastIndex);
      }

      if (optimizedResults.length > previousPromptsCount) {
        const shouldFollowLive =
          isConnector && connectorMultiAgentIsOptimizing && !connectorMultiAgentIsOptimized;
        const wasOnSecondToLast =
          previousPromptsCount > 0 && currentSelectedIterationIndex === previousPromptsCount - 1;

        if ((shouldFollowLive || wasOnSecondToLast) && optimizedResults[lastIndex]) {
          setCurrentSelectedIterationIndex(lastIndex);
          setCurrentSelectedPrompt(
            optimizedResults[lastIndex]?.['master-agent']?.prompt || 'Промпт отсутствует'
          );
          setCurrentSelectedPromptIndex(lastIndex.toString());
          setCurrentGraphSliderValue(lastIndex);
        }
      }

      setPreviousPromptsCount(optimizedResults.length);
    }
  }, [
    optimizedResults,
    currentSelectedIterationIndex,
    setCurrentSelectedIterationIndex,
    setCurrentSelectedPrompt,
    setCurrentSelectedPromptIndex,
    setCurrentGraphSliderValue,
    previousPromptsCount,
    isConnector,
    connectorMultiAgentIsOptimizing,
    connectorMultiAgentIsOptimized,
  ]);

  // Обработчик клавиатурной навигации
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (optimizedResults.length <= 1 || isAnimating) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          console.log('⌨️ ArrowLeft key pressed (should go BACKWARD)');
          event.preventDefault();
          prevPrompt();
          break;
        case 'ArrowRight':
          console.log('⌨️ ArrowRight key pressed (should go FORWARD)');
          event.preventDefault();
          nextPrompt();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [optimizedResults.length, isAnimating, currentSelectedIterationIndex]);

  // Анимация счетчика при изменении индекса
  useEffect(() => {
    setCounterKey(prev => prev + 1);
  }, [currentSelectedIterationIndex]);

  const nextPrompt = () => {
    console.log(`🖱️ nextPrompt called: currentIndex=${currentSelectedIterationIndex}, isAnimating=${isAnimating}`);
    if (optimizedResults.length > 0 && !isAnimating) {
      const newIndex = (currentSelectedIterationIndex + 1) % optimizedResults.length;
      console.log(`🔄 nextPrompt: moving from ${currentSelectedIterationIndex} to ${newIndex} (going FORWARD)`);
      setAnimationDirection('right');
      setAnimatingIndex(newIndex);
      setIsAnimating(true);
      
      // Обновляем ref для отслеживания состояния анимации
      animationRef.current = { isAnimating: true, animatingIndex: newIndex };
      
      // Анимируем переход: текущая уезжает влево, новая въезжает справа
      setTimeout(() => {
        // Проверяем, что анимация все еще активна (пользователь не прервал ее)
        if (animationRef.current.isAnimating && animationRef.current.animatingIndex === newIndex) {
          console.log(`🔄 nextPrompt animation complete, setting index to: ${newIndex}`);
          setCurrentSelectedIterationIndex(newIndex);
          setCurrentGraphSliderValue(newIndex);
          setCurrentSelectedPrompt(optimizedResults[newIndex]?.["master-agent"]?.prompt || 'Промпт отсутствует');
          setCurrentSelectedPromptIndex(newIndex.toString());
          setAnimatingIndex(null);
          setIsAnimating(false);
          animationRef.current = { isAnimating: false, animatingIndex: null };
        } else {
          console.log(`🔄 nextPrompt animation cancelled or interrupted`);
          setAnimatingIndex(null);
          setIsAnimating(false);
          animationRef.current = { isAnimating: false, animatingIndex: null };
        }
      }, 600);
    }
  };

  const prevPrompt = () => {
    console.log(`🖱️ prevPrompt called: currentIndex=${currentSelectedIterationIndex}, isAnimating=${isAnimating}`);
    if (optimizedResults.length > 0 && !isAnimating) {
      const newIndex = (currentSelectedIterationIndex - 1 + optimizedResults.length) % optimizedResults.length;
      console.log(`🔄 prevPrompt: moving from ${currentSelectedIterationIndex} to ${newIndex} (going BACKWARD)`);
      setAnimationDirection('left');
      setAnimatingIndex(newIndex);
      setIsAnimating(true);
      
      // Обновляем ref для отслеживания состояния анимации
      animationRef.current = { isAnimating: true, animatingIndex: newIndex };
      
      // Анимируем переход: текущая уезжает вправо, новая въезжает слева
      setTimeout(() => {
        // Проверяем, что анимация все еще активна (пользователь не прервал ее)
        if (animationRef.current.isAnimating && animationRef.current.animatingIndex === newIndex) {
          console.log(`🔄 prevPrompt animation complete, setting index to: ${newIndex}`);
          setCurrentSelectedIterationIndex(newIndex);
          setCurrentGraphSliderValue(newIndex);
          setCurrentSelectedPrompt(optimizedResults[newIndex]?.["master-agent"]?.prompt || 'Промпт отсутствует');
          setCurrentSelectedPromptIndex(newIndex.toString());
          setAnimatingIndex(null);
          setIsAnimating(false);
          animationRef.current = { isAnimating: false, animatingIndex: null };
        } else {
          console.log(`🔄 prevPrompt animation cancelled or interrupted`);
          setAnimatingIndex(null);
          setIsAnimating(false);
          animationRef.current = { isAnimating: false, animatingIndex: null };
        }
      }, 600);
    }
  };

  const currentIteration = optimizedResults[currentSelectedIterationIndex];

  const baselineMasterAgent = baselineResult?.["master-agent"];
  const baselineWorkers = Array.isArray(baselineResult?.["worker-agents"])
    ? (baselineResult?.["worker-agents"] as any[])
    : [];
  const baselineMetaPrompts =
    baselineResult?.["meta_prompts"] || baselineResult?.["metaPrompts"];

  // Initial configuration must be frozen from step 0 once it arrives.
  const getInitialConfigurationText = () => {
    return (
      baselineMasterAgent?.prompt ||
      currentCoordinatorPrompt ||
      'Введите промпт, чтобы начать оптимизацию'
    );
  };

 
  const initialTemperature =
    typeof baselineMasterAgent?.temperature === 'number'
      ? baselineMasterAgent.temperature
      : currentCoordinatorTemperature;

  const optimizedSlideTemperature = (() => {
    // Ищем результат по seq или iteration, соответствующий выбранному индексу
    // selectedIterationIndex соответствует индексу в optimizedResults (0 = первый оптимизированный = seq: 1)
    const seqForIndex = currentSelectedIterationIndex + 1; // seq: 1, 2, 3... для индексов 0, 1, 2...
    const iter = optimizedResults.find(r => r.seq === seqForIndex || r.iteration === seqForIndex);
    if (iter && typeof iter.temperature === 'number') return iter.temperature;
    if (typeof optimizedTemperature === 'number') return optimizedTemperature;
    return currentCoordinatorTemperature;
  })();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Свайпы для мобильных
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const dx = touchEndX.current - touchStartX.current;
    if (Math.abs(dx) > 40 && !isAnimating && optimizedResults.length > 1) {
      if (dx < 0) {
        console.log('👆 Swipe left detected (should go FORWARD)');
        nextPrompt(); // свайп влево
      }
      if (dx > 0) {
        console.log('👆 Swipe right detected (should go BACKWARD)');
        prevPrompt(); // свайп вправо
      }
    }
  };

  // Обработчик клика по агенту для Initial configuration
  const handleInitialAgentClick = (agentType: 'coordinator' | 'worker' | 'meta', agentIndex?: number, metaPromptKey?: string) => {
    setSelectedAgent(prev => ({
      ...prev,
      initial: { type: agentType, index: agentIndex, metaPromptKey }
    }));
  };

  // Обработчик клика по агенту для Optimized configuration
  const handleOptimizedAgentClick = (agentType: 'coordinator' | 'worker' | 'meta', agentIndex?: number, metaPromptKey?: string) => {
    console.log('🖱️ Agent clicked in optimized configuration:', {
      agentType,
      agentIndex,
      currentIterationIndex: currentSelectedIterationIndex,
      currentSelectedAgent: selectedAgent.optimized,
      currentIterationResultsLength: currentIterationResults.length,
      isAnimating,
      animatingIndex
    });
    
    // Прерываем любую активную анимацию при клике на агента
    if (isAnimating) {
      console.log(`🛑 Interrupting animation (isAnimating=${isAnimating}, animatingIndex=${animatingIndex})`);
      setAnimatingIndex(null);
      setIsAnimating(false);
      animationRef.current = { isAnimating: false, animatingIndex: null };
    }
    
    // Сохраняем текущий индекс итерации
    const currentIterationIndex = currentSelectedIterationIndex;
    
    // Проверяем, не изменился ли индекс до вызова setSelectedAgent
    console.log(`🔍 Before setSelectedAgent - currentSelectedIterationIndex: ${currentSelectedIterationIndex}`);
    
    setSelectedAgent(prev => ({
      ...prev,
      optimized: { type: agentType, index: agentIndex, metaPromptKey: metaPromptKey }
    }));
    
    // Проверяем, не изменился ли индекс после вызова setSelectedAgent
    console.log(`🔍 After setSelectedAgent - currentSelectedIterationIndex: ${currentSelectedIterationIndex}`);
    
    console.log('✅ Selected agent updated to:', { 
      type: agentType, 
      index: agentIndex,
      iterationIndexBefore: currentIterationIndex,
      iterationIndexAfter: currentSelectedIterationIndex
    });
  };

  // Initial side shows baseline snapshot (seq:0) when available, otherwise configurator state.
  const getInitialAgentData = (agentType: 'coordinator' | 'worker' | 'meta', agentIndex?: number, metaPromptKey?: string): AgentSettings => {
    if (agentType === 'meta' && metaPromptKey) {
      const metaPrompts = baselineMetaPrompts || (isConnector ? connectorMetaPrompts : undefined);
      const metaPrompt = metaPrompts?.[metaPromptKey as keyof typeof metaPrompts] || `Meta prompt: ${metaPromptKey}`;
      return {
        prompt: metaPrompt,
        model: 'N/A',
        temperature: 0,
        description: `Meta prompt configuration: ${metaPromptKey.replace(/_/g, ' ')}`,
        name: metaPromptKey.replace(/_/g, ' ')
      };
    }
    
    if (agentType === 'coordinator') {
      const baselineMaName = baselineMasterAgent?.name;
      const nameFromBaseline =
        typeof baselineMaName === 'string' && baselineMaName.trim() ? baselineMaName.trim() : '';
      return {
        prompt: baselineMasterAgent?.prompt || currentCoordinatorPrompt || 'You are a helpful assistant.',
        model: baselineMasterAgent?.model_name || currentCoordinatorModel || 'gpt-4o',
        temperature: Math.min(
          (typeof baselineMasterAgent?.temperature === 'number'
            ? baselineMasterAgent.temperature
            : currentCoordinatorTemperature) || 0.7,
          1
        ),
        description:
          baselineMasterAgent?.description ||
          currentCoordinatorDescription ||
          'Coordinator node that communicates with worker nodes to respond to user messages.',
        name: nameFromBaseline || currentCoordinatorName,
        reminder_prompt:
          (typeof baselineMasterAgent?.reminder_prompt === 'string'
            ? baselineMasterAgent.reminder_prompt
            : currentCoordinatorReminderPrompt) || undefined,
      };
    } else if (agentType === 'worker' && agentIndex !== undefined) {
      const baselineWorker = baselineWorkers[agentIndex];
      const worker = currentWorkers && currentWorkers[agentIndex];
      if (!baselineWorker && !worker) {
        return {
          prompt: 'You are a helpful assistant.',
          model: 'gpt-4o',
          temperature: 0.7,
          description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
          name: `Worker ${agentIndex + 1}`,
        };
      }
      return {
        prompt: baselineWorker?.prompt || worker?.prompt || 'You are a helpful assistant.',
        model: baselineWorker?.model_name || worker?.model || 'gpt-4o',
        temperature: Math.min(
          (typeof baselineWorker?.temperature === 'number'
            ? baselineWorker.temperature
            : worker?.temperature) || 0.7,
          1
        ),
        description:
          baselineWorker?.description ||
          worker?.description ||
          'AI agent that follows this prompt:\nYou are a helpful assistant.',
        name: baselineWorker?.name || worker?.name || `Worker ${agentIndex + 1}`,
        reminder_prompt:
          (typeof baselineWorker?.reminder_prompt === 'string'
            ? baselineWorker.reminder_prompt
            : worker?.reminder_prompt) || undefined,
      };
    }
    return {
      prompt: 'You are a helpful assistant.',
      model: 'gpt-4o',
      temperature: 0.7,
      description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
      name: 'Agent'
    };
  };

    // Мемоизированная функция для получения актуальных данных агента для Optimized configuration
  const getOptimizedAgentData = useMemo(() => {
    return (agentType: 'coordinator' | 'worker', agentIndex?: number): AgentSettings => {
      // Получаем результат из текущей выбранной итерации из optimizedResults
      const currentResult = currentSelectedIterationIndex >= 0 && currentSelectedIterationIndex < optimizedResults.length
        ? optimizedResults[currentSelectedIterationIndex]
        : undefined;
      
      if (currentResult) {
        
        if (agentType === 'coordinator' && currentResult["master-agent"]) {
          const ma = currentResult["master-agent"];
          const result = {
            prompt: ma.prompt || 'You are a helpful assistant.',
            model: ma.model_name || 'gpt-4o',
            temperature: Math.min(ma.temperature || 0.7, 1),
            description: ma.description || 'Coordinator node that communicates with worker nodes to respond to user messages.',
            name: ma.name || 'Координатор',
            reminder_prompt:
              typeof ma.reminder_prompt === 'string' ? ma.reminder_prompt : undefined,
          };
          return result;
        } else if (agentType === 'worker' && agentIndex !== undefined && currentResult["worker-agents"]) {
          const workerAgent = currentResult["worker-agents"][agentIndex];
          if (workerAgent) {
            const result = {
              prompt: workerAgent.prompt || 'You are a helpful assistant.',
              model: workerAgent.model_name || 'gpt-4o',
              temperature: Math.min(workerAgent.temperature || 0.7, 1),
              description: workerAgent.description || 'AI agent that follows this prompt:\nYou are a helpful assistant.',
              name: workerAgent.name || `Worker ${agentIndex + 1}`,
              reminder_prompt:
                typeof workerAgent.reminder_prompt === 'string'
                  ? workerAgent.reminder_prompt
                  : undefined,
            };
            return result;
          }
        }
      }
      
      // Fallback к исходным данным, если нет WebSocket результатов
      if (agentType === 'coordinator') {
        return {
          prompt: currentCoordinatorPrompt || 'You are a helpful assistant.',
          model: currentCoordinatorModel || 'gpt-4o',
          temperature: Math.min(optimizedTemperature || currentCoordinatorTemperature || 0.7, 1),
          description: currentCoordinatorDescription || 'Coordinator node that communicates with worker nodes to respond to user messages.',
          name: currentCoordinatorName,
          reminder_prompt: currentCoordinatorReminderPrompt || undefined,
        };
      } else if (agentType === 'worker' && agentIndex !== undefined && currentWorkers && currentWorkers[agentIndex]) {
        const worker = currentWorkers[agentIndex];
        return {
          prompt: worker.prompt || 'You are a helpful assistant.',
          model: worker.model || 'gpt-4o',
          temperature: Math.min(optimizedTemperature || worker.temperature || 0.7, 1),
          description: worker.description || 'AI agent that follows this prompt:\nYou are a helpful assistant.',
          name: worker.name || `Worker ${agentIndex + 1}`,
          reminder_prompt: worker.reminder_prompt,
        };
      }

      return {
        prompt: 'You are a helpful assistant.',
        model: 'gpt-4o',
        temperature: 0.7,
        description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
        name: 'Agent'
      };
    };
  }, [optimizedResults, currentSelectedIterationIndex, currentCoordinatorPrompt, currentCoordinatorModel, currentCoordinatorTemperature, currentCoordinatorDescription, currentCoordinatorReminderPrompt, currentCoordinatorName, optimizedTemperature, currentWorkers]);

  // Мемоизированные данные для текущего выбранного агента
  const currentAgentData = useMemo(() => {
    
    if (!selectedAgent.optimized) {
      return {
        prompt: 'You are a helpful assistant.',
        model: 'gpt-4o',
        temperature: 0.7,
        description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
        name: 'Agent'
      };
    }
    
    // Если выбран метапромпт, возвращаем специальную структуру
    if (selectedAgent.optimized.type === 'meta' && selectedAgent.optimized.metaPromptKey) {
      const currentResult = optimizedResults[currentSelectedIterationIndex];
      // Пробуем получить из meta_prompts или metaPrompts (поддержка обоих форматов)
      const metaPrompts = currentResult?.["meta_prompts"] || currentResult?.["metaPrompts"];
      const metaPrompt = metaPrompts?.[selectedAgent.optimized.metaPromptKey as keyof typeof metaPrompts] || `Meta prompt: ${selectedAgent.optimized.metaPromptKey}`;
      return {
        prompt: metaPrompt,
        model: 'N/A',
        temperature: 0,
        description: `Meta prompt configuration: ${selectedAgent.optimized.metaPromptKey.replace(/_/g, ' ')}`,
        name: selectedAgent.optimized.metaPromptKey.replace(/_/g, ' ')
      };
    }
    
    // Получаем результат из текущей выбранной итерации из optimizedResults
    const currentResult = currentSelectedIterationIndex >= 0 && currentSelectedIterationIndex < optimizedResults.length
      ? optimizedResults[currentSelectedIterationIndex]
      : undefined;
    
    if (currentResult) {
      if (selectedAgent.optimized.type === 'coordinator' && currentResult["master-agent"]) {
        const ma = currentResult["master-agent"];
        return {
          prompt: ma.prompt || 'You are a helpful assistant.',
          model: ma.model_name || 'gpt-4o',
          temperature: Math.min(ma.temperature || 0.7, 1),
          description: ma.description || 'Coordinator node that communicates with worker nodes to respond to user messages.',
          name: ma.name || 'Координатор',
          reminder_prompt:
            typeof ma.reminder_prompt === 'string' ? ma.reminder_prompt : undefined,
        };
      } else if (selectedAgent.optimized.type === 'worker' && selectedAgent.optimized.index !== undefined && currentResult["worker-agents"]) {
        const workerAgent = currentResult["worker-agents"][selectedAgent.optimized.index];
        if (workerAgent) {
          return {
            prompt: workerAgent.prompt || 'You are a helpful assistant.',
            model: workerAgent.model_name || 'gpt-4o',
            temperature: Math.min(workerAgent.temperature || 0.7, 1),
            description: workerAgent.description || 'AI agent that follows this prompt:\nYou are a helpful assistant.',
            name: workerAgent.name || `Worker ${selectedAgent.optimized.index + 1}`,
            reminder_prompt:
              typeof workerAgent.reminder_prompt === 'string'
                ? workerAgent.reminder_prompt
                : undefined,
          };
        }
      }
    }
    
    // Fallback к исходным данным, если нет WebSocket результатов
    if (selectedAgent.optimized.type === 'coordinator') {
      return {
        prompt: currentCoordinatorPrompt || 'You are a helpful assistant.',
        model: currentCoordinatorModel || 'gpt-4o',
        temperature: Math.min(optimizedTemperature || currentCoordinatorTemperature || 0.7, 1),
        description: currentCoordinatorDescription || 'Coordinator node that communicates with worker nodes to respond to user messages.',
        name: currentCoordinatorName,
        reminder_prompt: currentCoordinatorReminderPrompt || undefined,
      };
    } else if (selectedAgent.optimized.type === 'worker' && selectedAgent.optimized.index !== undefined && currentWorkers && currentWorkers[selectedAgent.optimized.index]) {
      const worker = currentWorkers[selectedAgent.optimized.index];
      return {
        prompt: worker.prompt || 'You are a helpful assistant.',
        model: worker.model || 'gpt-4o',
        temperature: Math.min(optimizedTemperature || worker.temperature || 0.7, 1),
        description: worker.description || 'AI agent that follows this prompt:\nYou are a helpful assistant.',
        name: worker.name || `Worker ${selectedAgent.optimized.index + 1}`,
        reminder_prompt: worker.reminder_prompt,
      };
    }

    return {
      prompt: 'You are a helpful assistant.',
      model: 'gpt-4o',
      temperature: 0.7,
      description: 'AI agent that follows this prompt:\nYou are a helpful assistant.',
      name: 'Agent'
    };
  }, [selectedAgent.optimized, optimizedResults, currentSelectedIterationIndex, currentCoordinatorPrompt, currentCoordinatorModel, currentCoordinatorTemperature, currentCoordinatorDescription, currentCoordinatorReminderPrompt, currentCoordinatorName, optimizedTemperature, currentWorkers]);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= optimizedResults.length || index === currentSelectedIterationIndex) {
        return;
      }
      setCurrentSelectedIterationIndex(index);
      setCurrentGraphSliderValue(index);
      setCurrentSelectedPrompt(
        optimizedResults[index]?.['master-agent']?.prompt || 'Промпт отсутствует'
      );
      setCurrentSelectedPromptIndex(index.toString());
    },
    [
      optimizedResults,
      currentSelectedIterationIndex,
      setCurrentSelectedIterationIndex,
      setCurrentGraphSliderValue,
      setCurrentSelectedPrompt,
      setCurrentSelectedPromptIndex,
    ]
  );

  const handleConnectorInitialAgentClick = useCallback(
    (
      agentType: 'coordinator' | 'worker' | 'meta',
      agentIndex?: number,
      metaPromptKey?: string
    ) => {
      setSelectedAgent((prev) => ({
        ...prev,
        initial: { type: agentType, index: agentIndex, metaPromptKey },
      }));
    },
    []
  );

  const handleConnectorOptimizedAgentClick = useCallback(
    (
      agentType: 'coordinator' | 'worker' | 'meta',
      agentIndex?: number,
      metaPromptKey?: string
    ) => {
      if (isAnimating) {
        setAnimatingIndex(null);
        setIsAnimating(false);
        animationRef.current = { isAnimating: false, animatingIndex: null };
      }
      setSelectedAgent((prev) => ({
        ...prev,
        optimized: { type: agentType, index: agentIndex, metaPromptKey },
      }));
    },
    [isAnimating]
  );

  if (isConnector) {
    const connectorEvaluationReady =
      currentIterationResults.length > 0 || connectorMultiAgentIsOptimizing;

    const initialAgent = selectedAgent.initial || { type: 'coordinator' as const };
    const optimizedAgent = selectedAgent.optimized || { type: 'coordinator' as const };
    const initialRaw = connectorEvaluationReady
      ? getInitialAgentData(
          initialAgent.type,
          initialAgent.index,
          initialAgent.metaPromptKey
        )
      : null;

    const initialWorkerMode =
      initialRaw &&
      initialAgent.type === 'worker' &&
      initialAgent.index !== undefined
        ? baselineResult
          ? baselineWorkers[initialAgent.index]?.mode ||
            currentWorkers?.[initialAgent.index]?.mode ||
            'synchronous'
          : currentWorkers?.[initialAgent.index]?.mode || 'synchronous'
        : undefined;

    const optimizedRaw =
      connectorEvaluationReady && optimizedResults.length > 0
        ? {
            ...currentAgentData,
            mode:
              optimizedAgent.type === 'worker' && optimizedAgent.index !== undefined
                ? optimizedResults[currentSelectedIterationIndex]?.['worker-agents']?.[
                    optimizedAgent.index
                  ]?.mode ||
                  currentWorkers?.[optimizedAgent.index]?.mode ||
                  'asynchronous'
                : undefined,
          }
        : null;

    const currentStepResult = optimizedResults[currentSelectedIterationIndex];
    const currentStepSeq =
      currentStepResult?.seq !== undefined
        ? currentStepResult.seq
        : currentStepResult?.iteration !== undefined
          ? currentStepResult.iteration
          : currentSelectedIterationIndex + 1;

    const optimizedWorkerCount =
      currentStepResult?.['worker-agents']?.length ||
      baselineWorkers.length ||
      currentWorkers?.length ||
      0;

    const initialWorkerCount = Math.max(
      baselineWorkers.length,
      currentWorkers?.length || 0
    );

    return (
      <ConnectorConfigEvaluationView
        evaluationReady={connectorEvaluationReady}
        selectedInitialAgent={initialAgent}
        selectedOptimizedAgent={optimizedAgent}
        onInitialAgentClick={handleConnectorInitialAgentClick}
        onOptimizedAgentClick={handleConnectorOptimizedAgentClick}
        initialWorkersCount={initialWorkerCount}
        optimizedWorkersCount={optimizedWorkerCount}
        optimizedIterationIndex={currentSelectedIterationIndex}
        initialData={
          initialRaw
            ? {
                name: initialRaw.name,
                prompt: initialRaw.prompt,
                model: initialRaw.model,
                temperature: initialRaw.temperature,
                description: initialRaw.description,
                reminder_prompt: initialRaw.reminder_prompt,
                mode: initialWorkerMode,
              }
            : null
        }
        initialPromptLive={connectorEvaluationReady && !initialPrompt && !!botPrompt}
        optimizedData={
          optimizedRaw
            ? {
                name: optimizedRaw.name,
                prompt: optimizedRaw.prompt,
                model: optimizedRaw.model,
                temperature: optimizedRaw.temperature,
                description: optimizedRaw.description,
                reminder_prompt: optimizedRaw.reminder_prompt,
                mode: optimizedRaw.mode,
              }
            : null
        }
        optimizedResultsCount={optimizedResults.length}
        currentStepIndex={currentSelectedIterationIndex}
        currentStepSeq={currentStepSeq}
        isAnimating={isAnimating}
        onPrevStep={prevPrompt}
        onNextStep={nextPrompt}
        onSelectStep={goToStep}
        onExportStep={
          onLoadOptimizedStepIntoConfigurator && currentStepResult
            ? () => onLoadOptimizedStepIntoConfigurator(currentStepResult)
            : undefined
        }
      />
    );
  }

  return (
    <section className="config-visualization" id="visualization">
      <div className="evaluation-visualization-block">
        <h2 className="section-title">Оценка конфигурации</h2>
        
        <div className="prompt-cards">
          {/* Initial Prompt Card */}
          <div 
            className="prompt-card before-card"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <h3 className="card-title">Исходная конфигурация</h3>
            
            <div className="card-content">
              {/* Новый конфигуратор для выбора агентов */}
              <AgentSelector 
                variant="initial" 
                onAgentClick={handleInitialAgentClick}
                selectedAgent={selectedAgent.initial}
                workersCount={
                  baselineResult?.["worker-agents"]?.length || 
                  optimizedResults[0]?.["worker-agents"]?.length || 
                  currentWorkers?.length || 
                  0
                }
                isConnector={isConnector}
              />
              
              {/* Информация об агенте над промптом */}
              {selectedAgent.initial && (
                <div className="agent-info-header">
                  <div className="agent-name">
                    <span className="agent-name-label">Агент:</span>
                    <span className="agent-name-value">
                      {getInitialAgentData(
                        selectedAgent.initial.type,
                        selectedAgent.initial.index,
                        selectedAgent.initial.metaPromptKey
                      ).name}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Промпт с пояснением */}
              <div className="prompt-section">
                <div className="prompt-label">Промпт:</div>
                <div className={`prompt-text ${!initialPrompt && botPrompt ? 'live-update' : ''}`}>
                  {selectedAgent.initial 
                    ? getInitialAgentData(selectedAgent.initial.type, selectedAgent.initial.index, selectedAgent.initial.metaPromptKey).prompt
                    : getInitialConfigurationText()}
                </div>
              </div>

              {isConnector &&
                selectedAgent.initial &&
                selectedAgent.initial.type !== 'meta' && (
                  <div className="description-section">
                    <div className="description-label">Промпт-напоминание:</div>
                    <div className="description-text">
                      {formatReminderDisplay(
                        getInitialAgentData(
                          selectedAgent.initial.type,
                          selectedAgent.initial.index,
                          selectedAgent.initial.metaPromptKey
                        ).reminder_prompt
                      )}
                    </div>
                  </div>
                )}
              
              {/* Описание агента */}
              {selectedAgent.initial && (
                <div className="description-section">
                  <div className="description-label">Описание:</div>
                  <div className="description-text">
                    {getInitialAgentData(selectedAgent.initial.type, selectedAgent.initial.index, selectedAgent.initial.metaPromptKey).description}
                  </div>
                </div>
              )}
            </div>
            
            {/* Информация о конфигурации */}
            {selectedAgent.initial && selectedAgent.initial.type !== 'meta' && (
              <div className="config-info">
                <div className="config-info-item">
                  <span className="config-label">Модель:</span>
                  <span className="config-value">{getInitialAgentData(selectedAgent.initial.type, selectedAgent.initial.index).model}</span>
                </div>
                <div className="config-info-item">
                  <span className="config-label">Температура:</span>
                  <span className="config-value">{getInitialAgentData(selectedAgent.initial.type, selectedAgent.initial.index).temperature.toFixed(2)}</span>
                </div>
                {selectedAgent.initial.type === 'worker' && selectedAgent.initial.index !== undefined && currentWorkers && currentWorkers[selectedAgent.initial.index] && (
                  <div className="config-info-item">
                    <span className="config-label">Режим:</span>
                    <span className="config-value">
                      {/* До получения seq 0 все агенты в synchronous */}
                      {baselineResult ? (currentWorkers[selectedAgent.initial.index].mode || 'synchronous') : 'synchronous'}
                    </span>
                  </div>
                )}
              </div>
            )}
            {!selectedAgent.initial && (
              <div className="config-info">
                <div className="config-info-item">
                  <span className="config-label">Модель:</span>
                  <span className="config-value">{selectedModel || 'Не выбран'}</span>
                </div>
                <div className="config-info-item">
                  <span className="config-label">Температура:</span>
                  <span className="config-value">{typeof initialTemperature === 'number' ? initialTemperature.toFixed(2) : '0.00'}</span>
                </div>
                {/* For initial configuration use step-0 meta prompts when available */}
                {(() => {
                  const metaPrompts = baselineMetaPrompts || (isConnector ? connectorMetaPrompts : undefined);
                  
                  if (metaPrompts && Object.keys(metaPrompts).length > 0) {
                    return (
                      <div className="config-info-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span className="config-label">Мета-промпты:</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                          {sortMetaPromptKeys(Object.keys(metaPrompts)).map((key) => (
                            <span 
                              key={key} 
                              className="config-value" 
                              style={{ 
                                padding: '2px 8px', 
                                background: '#E3F0FB', 
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}
                            >
                              {key.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Пустое место для симметрии с Optimized configuration */}
            <div className="carousel-controls-placeholder"></div>
          </div>

          {/* Optimized Prompt Card (карусель) */}
          <div 
            className="prompt-card after-card"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <h3 className="card-title">Оптимизированная конфигурация</h3>
            
            <div className="card-content">
              <div className="carousel-container">
                {/* Показываем пустое состояние до получения seq 1 */}
                {optimizedResults.length === 0 ? (
                  <div className="carousel-slide active">
                    <div className="prompt-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', textAlign: 'center' }}>
                      <div className="prompt-label" style={{ marginBottom: '12px' }}>Оптимизация ещё не запускалась.</div>
                      <div className="prompt-text placeholder-text" style={{ color: '#7F9AB3', fontSize: '14px' }}>
                        Оптимизированная конфигурация появится здесь после первого шага оптимизации.
                      </div>
                    </div>
                  </div>
                ) : (
                  optimizedResults.map((result, index) => (
                    <div
                      key={index}
                      className={`carousel-slide ${index === currentSelectedIterationIndex ? 'active' : ''} ${isAnimating && index === currentSelectedIterationIndex ? `slide-out-${animationDirection}` : ''} ${isAnimating && index === animatingIndex ? `animating-${animationDirection}` : ''}`}
                      style={{
                        transform: !isAnimating ? `translateX(${(index - currentSelectedIterationIndex) * 100}%)` : 'none',
                        opacity: index === currentSelectedIterationIndex || index === animatingIndex ? 1 : 0,
                        visibility: index === currentSelectedIterationIndex || index === animatingIndex ? 'visible' : 'hidden',
                        zIndex: index === animatingIndex ? 10 : index === currentSelectedIterationIndex ? 5 : 1,
                      }}
                                         >
                       {/* Новый конфигуратор для выбора агентов */}
                       <AgentSelector
                         isConnector={isConnector} 
                         variant="optimized" 
                         onAgentClick={handleOptimizedAgentClick}
                         selectedAgent={selectedAgent.optimized}
                         workersCount={result["worker-agents"]?.length || 0}
                         iterationIndex={index}
                       />
                       
                       {/* Export this optimization step into Integration Configurator */}
                       {isConnector &&
                         onLoadOptimizedStepIntoConfigurator &&
                         result?.["master-agent"] && (
                           <button
                             type="button"
                             className="connector-load-step-into-config-btn"
                             style={{
                               position: 'absolute',
                               top: 12,
                               right: 12,
                               padding: '6px 12px',
                               fontSize: 12,
                               zIndex: 50
                             }}
                             onClick={(e) => {
                               e.preventDefault();
                               e.stopPropagation();
                               onLoadOptimizedStepIntoConfigurator(result);
                             }}
                             aria-label={`Export step ${result?.seq ?? result?.iteration ?? index + 1} to the Integration Configurator`}
                             title={`Export step ${result?.seq ?? result?.iteration ?? index + 1} to the Integration Configurator`}
                           >
                             <i className="fas fa-upload" aria-hidden /> Экспорт
                           </button>
                         )}
                       
                       {/* Информация об агенте над промптом */}
                       {selectedAgent.optimized && (
                         <div className="agent-info-header">
                           <div className="agent-name">
                             <span className="agent-name-label">Агент:</span>
                             <span className="agent-name-value">
                               {selectedAgent.optimized.type === 'coordinator' ? 
                                (currentAgentData?.name || 'Координатор') : 
                                selectedAgent.optimized.type === 'worker' && selectedAgent.optimized.index !== undefined ? 
                                (currentAgentData?.name || `Worker ${selectedAgent.optimized.index + 1}`) :
                                selectedAgent.optimized.type === 'meta' && selectedAgent.optimized.metaPromptKey ?
                                (selectedAgent.optimized.metaPromptKey.replace(/_/g, ' ')) :
                                'Agent'}
                             </span>
                           </div>
                         </div>
                       )}
                       
                       {/* Промпт с пояснением */}
                       <div className="prompt-section">
                         <div className="prompt-label">Промпт:</div>
                         <div className="prompt-text">
                          {selectedAgent.optimized 
                            ? (selectedAgent.optimized.type === 'meta' && selectedAgent.optimized.metaPromptKey
                                ? (() => {
                                    // Используем данные из текущего слайда карусели (result), а не из currentAgentData
                                    // Поддерживаем оба формата: meta_prompts и metaPrompts
                                    const metaPrompts = result["meta_prompts"] || result["metaPrompts"];
                                    if (metaPrompts && typeof metaPrompts === 'object') {
                                      const metaPromptValue = (metaPrompts as Record<string, any>)[selectedAgent.optimized.metaPromptKey];
                                      if (metaPromptValue && typeof metaPromptValue === 'string' && metaPromptValue.trim().length > 0) {
                                        return metaPromptValue;
                                      }
                                    }
                                    return `Meta prompt: ${selectedAgent.optimized.metaPromptKey}`;
                                  })()
                                : (currentAgentData?.prompt || 'Промпт отсутствует'))
                            : (result["master-agent"]?.prompt || 'Промпт отсутствует')}
                         </div>
                       </div>

                       {isConnector &&
                         selectedAgent.optimized &&
                         selectedAgent.optimized.type !== 'meta' && (
                           <div className="description-section">
                             <div className="description-label">Промпт-напоминание:</div>
                             <div className="description-text">
                               {formatReminderDisplay(currentAgentData?.reminder_prompt)}
                             </div>
                           </div>
                         )}
                       
                       {/* Описание агента */}
                       {selectedAgent.optimized && selectedAgent.optimized.type !== 'meta' && (
                         <div className="description-section">
                           <div className="description-label">Описание:</div>
                           <div className="description-text">
                             {currentAgentData?.description || 'Описания нет'}
                           </div>
                         </div>
                       )}
                       {/* Описание для метапромпта */}
                       {selectedAgent.optimized && selectedAgent.optimized.type === 'meta' && selectedAgent.optimized.metaPromptKey && (
                         <div className="description-section">
                           <div className="description-label">Мета-промпт:</div>
                           <div className="description-text">
                             {selectedAgent.optimized.metaPromptKey.replace(/_/g, ' ')}
                           </div>
                         </div>
                       )}
                     </div>
                  ))
                )}
              </div>

            </div>
            
            {/* Информация о конфигурации - показываем только если есть оптимизированные результаты */}
            {optimizedResults.length > 0 && (
              <div className="config-info">
                <div className="config-info-item">
                  <span className="config-label">Модель:</span>
                  <span className="config-value">{selectedAgent.optimized ? (currentAgentData?.model || 'Не выбран') : (selectedModel || 'Не выбран')}</span>
                </div>
                <div className="config-info-item">
                  <span className="config-label">Температура:</span>
                  <span className="config-value">{selectedAgent.optimized ? (currentAgentData?.temperature?.toFixed(2) || '0.00') : (typeof optimizedSlideTemperature === 'number' ? optimizedSlideTemperature.toFixed(2) : '0.00')}</span>
                </div>
                {selectedAgent.optimized && selectedAgent.optimized.type === 'worker' && selectedAgent.optimized.index !== undefined && (
                  (() => {
                    const currentResult = optimizedResults[currentSelectedIterationIndex];
                    const workerAgent = currentResult?.["worker-agents"]?.[selectedAgent.optimized.index];
                    const mode = workerAgent?.mode || currentWorkers?.[selectedAgent.optimized.index]?.mode || 'asynchronous';
                    return (
                      <div className="config-info-item">
                        <span className="config-label">Режим:</span>
                        <span className="config-value">{mode}</span>
                      </div>
                    );
                  })()
                )}
                {selectedAgent.optimized && selectedAgent.optimized.type === 'meta' && selectedAgent.optimized.metaPromptKey && (
                  <div className="config-info-item">
                    <span className="config-label">Мета-промпт:</span>
                    <span className="config-value">{selectedAgent.optimized.metaPromptKey.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {/* Показываем все metaPrompts для optimized configuration, если они есть в текущем результате */}
                {optimizedResults.length > 0 && (() => {
                  const currentResult = optimizedResults[currentSelectedIterationIndex];
                  const metaPrompts = currentResult?.["meta_prompts"] || currentResult?.["metaPrompts"];
                  
                  if (metaPrompts && typeof metaPrompts === 'object' && Object.keys(metaPrompts).length > 0) {
                    return (
                      <div className="config-info-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span className="config-label">Мета-промпты:</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                          {sortMetaPromptKeys(Object.keys(metaPrompts)).map((key) => (
                            <span 
                              key={key} 
                              className="config-value" 
                              style={{ 
                                padding: '2px 8px', 
                                background: '#E3F0FB', 
                                borderRadius: '4px',
                                fontSize: '12px',
                                border: '1px solid #FF8300'
                              }}
                            >
                              {key.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {isConnector &&
              onLoadOptimizedStepIntoConfigurator &&
              optimizedResults.length > 0 && (
                <div className="connector-optimized-steps-load">
                  <div className="connector-optimized-steps-load-title">Шаги оптимизации</div>
                  <div className="connector-optimized-steps-load-list">
                    {optimizedResults.map((stepResult, index) => {
                      const seq =
                        stepResult.seq !== undefined
                          ? stepResult.seq
                          : stepResult.iteration !== undefined
                            ? stepResult.iteration
                            : index + 1;
                      return (
                        <div key={index} className="connector-optimized-step-load-row">
                          <span className="connector-optimized-step-load-label">Шаг {seq}</span>
                          <button
                            type="button"
                            className="connector-load-step-into-config-btn"
                            onClick={() => onLoadOptimizedStepIntoConfigurator(stepResult)}
                            title="Экспортировать этот шаг в конфигуратор интеграции (поля выше можно править)"
                          >
                            <i className="fas fa-arrow-up" aria-hidden />
                            Экспорт в конфигуратор
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Если есть несколько оптимизированных результатов — показываем карусель */}
            {optimizedResults.length > 1 && (
              <div className="carousel-controls">
                <button 
                  className="carousel-btn prev-btn"
                  onClick={(e) => {
                    console.log('🖱️ PREV BUTTON clicked (should go BACKWARD)');
                    console.log('🔍 PREV button event:', e.target);
                    prevPrompt();
                  }}
                  disabled={isAnimating}
                  aria-label="Предыдущий промпт"
                  title="Предыдущий промпт (←)"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <div className="carousel-indicators">
                  {optimizedResults.map((_, index) => (
                    <button
                      key={index}
                      className={`indicator-dot ${index === currentSelectedIterationIndex ? 'active' : ''} ${newPromptIndex === index ? 'new' : ''}`}
                      onClick={() => {
                        console.log(`🖱️ Carousel indicator clicked: index=${index}, currentIndex=${currentSelectedIterationIndex}, isAnimating=${isAnimating}`);
                        if (!isAnimating && index !== currentSelectedIterationIndex) {
                          console.log(`🔄 Starting carousel animation to index: ${index}`);
                          setAnimationDirection(index > currentSelectedIterationIndex ? 'right' : 'left');
                          setAnimatingIndex(index);
                          setIsAnimating(true);
                          
                          // Обновляем ref для отслеживания состояния анимации
                          animationRef.current = { isAnimating: true, animatingIndex: index };
                          
                          setTimeout(() => {
                            // Проверяем, что анимация все еще активна (пользователь не прервал ее)
                            if (animationRef.current.isAnimating && animationRef.current.animatingIndex === index) {
                              console.log(`🔄 Carousel animation complete, setting index to: ${index}`);
                              setCurrentSelectedIterationIndex(index);
                              setCurrentGraphSliderValue(index);
                              setCurrentSelectedPrompt(optimizedResults[index]?.["master-agent"]?.prompt || 'Промпт отсутствует');
                              setCurrentSelectedPromptIndex(index.toString());
                              setAnimatingIndex(null);
                              setIsAnimating(false);
                              animationRef.current = { isAnimating: false, animatingIndex: null };
                            } else {
                              console.log(`🔄 Carousel animation cancelled or interrupted`);
                              setAnimatingIndex(null);
                              setIsAnimating(false);
                              animationRef.current = { isAnimating: false, animatingIndex: null };
                            }
                          }, 600);
                        }
                      }}
                      disabled={isAnimating}
                      aria-label={`Go to prompt ${index + 1}`}
                    />
                  ))}
                </div>
                <button 
                  className="carousel-btn next-btn"
                  onClick={(e) => {
                    console.log('🖱️ NEXT BUTTON clicked (should go FORWARD)');
                    console.log('🔍 NEXT button event:', e.target);
                    nextPrompt();
                  }}
                  disabled={isAnimating}
                  aria-label="Следующий промпт"
                  title="Следующий промпт (→)"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiAgentConfigVisualization;
